import React, { useReducer, useContext, useEffect, useRef, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { categoryReducer } from './categoryReducer';
import {
	ICategoryV2,
	IGetPaginatedCategoriesV2,
	TCategoryType,
	SortByForPaginatedCategories,
	DirectionForPaginated,
} from 'types';
import { PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { CategoryActionTypes } from './categoryActions';
import {
	categoryInitialState,
	CategoryContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './categoryContext';
import { getPaginatedCategoriesV2, getCategoryPhotos, deleteCategoryV2 } from 'backend/categories';
import { createAppError } from 'utils/helpers';
import { AxiosResponse } from 'axios';
import { useFetchData } from 'hooks/useFetchData';

export const CategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(categoryReducer, categoryInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<ICategoryV2>, IGetPaginatedCategoriesV2>(
		isMounted.current && state.IDSelectedCustomer ? getPaginatedCategoriesV2 : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchInput: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer || '',
			restaurantId: state.IDSelectedRestaurant,
			types: state.IDsSelectedCategoryTypes,
			sorting: state.sortBy
				? {
						sortBy: {
							'CategoriesA-Z': SortByForPaginatedCategories.Name,
							'CategoriesZ-A': SortByForPaginatedCategories.Name,
							'CategoryTypeA-Z': SortByForPaginatedCategories.Type,
							'CategoryTypeZ-A': SortByForPaginatedCategories.Type,
						}[state.sortBy],
						direction: {
							'CategoriesA-Z': DirectionForPaginated.Ascending,
							'CategoriesZ-A': DirectionForPaginated.Descending,
							'CategoryTypeA-Z': DirectionForPaginated.Ascending,
							'CategoryTypeZ-A': DirectionForPaginated.Descending,
						}[state.sortBy],
				  }
				: null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: CategoryActionTypes.SET_CATEGORIES,
				payload: {
					categories: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);

			const getCategoriesPreSignedUrl = async (categoryIds: string[]) => {
				try {
					const categoryPhotosResponse = await getCategoryPhotos({ categoryIds, size: 1 });
					const updatedCategories = response.data.items.reduce((acc: ICategoryV2['items'], item) => {
						const categoryPhotoFile = categoryPhotosResponse.data.files.find(
							(photoFile) => photoFile.id === item.id,
						);
						acc.push({
							...item,
							preSignedUrl: categoryPhotoFile ? categoryPhotoFile.preSignedUrl : null,
						});
						return acc;
					}, []);

					dispatch({
						type: CategoryActionTypes.SET_CATEGORIES,
						payload: {
							categories: { totalCount: response.data.totalCount, items: updatedCategories },
							typeOfModalWindow: state.typeOfModalWindow,
						},
					});
				} catch (err) {
					const error = err as Error;
					const appError = createAppError(error);
					addAppError(appError);
				}
			};

			const categoryIds = response.data.items.map((category) => category.id);
			getCategoriesPreSignedUrl(categoryIds);
		}
	}, [response]);

	useEffect(() => {
		if (state.needRefetchData) {
			refetch();
			dispatch({
				type: CategoryActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	const refetchData = (value: boolean) => {
		dispatch({
			type: CategoryActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	};

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteCategoryV2(state.IDForDeletion);
			const filteredCategories = state.categories.items.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: CategoryActionTypes.SET_CATEGORIES,
				payload: {
					categories: {
						totalCount: state.categories.totalCount - 1,
						items: filteredCategories,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: CategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: CategoryActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const toggleRememberMyChoice = () => {
		!state.rememberMyChoice && state.IDSelectedCustomer
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, state.IDSelectedCustomer)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: CategoryActionTypes.TOGGLE_REMEMBER_MY_CHOICE,
			payload: {
				rememberMyChoice: !state.rememberMyChoice,
			},
		});
	};

	const setIDCustomer = (value: Nullable<string>) => {
		value && state.rememberMyChoice
			? localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value)
			: localStorage.removeItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU);

		dispatch({
			type: CategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
				categories: value ? state.categories : { totalCount: 0, items: [] },
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: CategoryActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
			},
		});
	};

	const setIDsSelectedCategoryTypes = (value: TCategoryType[]) => {
		dispatch({
			type: CategoryActionTypes.SET_IDS_SELECTED_CATEGORY_TYPES,
			payload: {
				IDsSelectedCategoryTypes: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: CategoryActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: CategoryActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: CategoryActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: CategoryActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: CategoryActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForAssignRestaurants = (value: string) => {
		dispatch({
			type: CategoryActionTypes.SET_ID_FOR_ASSIGN_RESTAURANTS,
			payload: {
				IDForAssignRestaurants: value,
				typeOfModalWindow: value ? 'assignRestaurants' : null,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: CategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		!isMounted.current &&
			value.IDSelectedCustomer &&
			localStorage.setItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU, value.IDSelectedCustomer);

		dispatch({
			type: CategoryActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: {
					...value,
					IDSelectedCustomer:
						value.IDSelectedCustomer || localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU),
				},
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<CategoryContext.Provider
			value={{
				refetchData,
				deleteSelectedItem,
				changeSearchString,
				toggleRememberMyChoice,
				setIDCustomer,
				setIDRestaurant,
				setIDsSelectedCategoryTypes,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForAssignRestaurants,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				needRefetchData: state.needRefetchData,
				categories: state.categories,
				searchString: state.searchString,
				rememberMyChoice: state.rememberMyChoice,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDsSelectedCategoryTypes: state.IDsSelectedCategoryTypes,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForAssignRestaurants: state.IDForAssignRestaurants,
				isInitializedSelectCustomerModal: state.isInitializedSelectCustomerModal,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</CategoryContext.Provider>
	);
};
